import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { loginSuccess, authCheckComplete } from '../../store/slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8009';

/**
 * Landing page for the GitHub OAuth callback.
 * Backend redirects here as: /auth/callback?token=<jwt>
 *
 * We call /auth/me directly (not via RTK Query) so we can pass
 * the token without waiting for the Redux store to update.
 */
export const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showVerified, setShowVerified] = useState(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const token = searchParams.get('token');
    const verified = searchParams.get('verified') === '1';

    if (!token) {
      dispatch(authCheckComplete());
      navigate('/auth/login', { replace: true });
      return;
    }

    if (verified) {
      setShowVerified(true);
    }

    fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch user');
        return res.json();
      })
      .then((user) => {
        localStorage.setItem('accessToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        dispatch(loginSuccess({ user, accessToken: token }));

        const delay = verified ? 1500 : 0;
        setTimeout(() => {
          if (!user.installation_id) {
            navigate('/onboarding/github-install', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }, delay);
      })
      .catch(() => {
        setErrorMsg('Sign-in failed. Redirecting…');
        setTimeout(() => navigate('/auth/login', { replace: true }), 2000);
      });
  }, [dispatch, navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      {errorMsg ? (
        <Typography color="error">{errorMsg}</Typography>
      ) : showVerified ? (
        <>
          <CheckCircle sx={{ fontSize: 52, color: 'success.main' }} />
          <Typography variant="h6" fontWeight={700}>Email verified!</Typography>
          <Typography color="text.secondary">Taking you to your account…</Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">Signing you in…</Typography>
        </>
      )}
    </Box>
  );
};
